import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatEur } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { BrandHeader } from "@/components/BrandHeader";
import { ADULT_CATEGORIES, FAMILY_CATEGORIES } from "@/lib/ageCategories";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, LogOut, UserPlus, LogIn } from "lucide-react";

const inputCls = "w-full rounded-xl border-2 border-slate-200 focus:border-[#003284] outline-none px-4 py-2.5 transition-colors";
export const COMPANY_GROUPS = ["GERE", "ACUDE", "BUTANO", "PETRÓLEO", "OTRO"];

export default function CuentaPage() {
  const { userToken, user, loginUser, logoutUser, setUser } = useApp();
  const [sp] = useSearchParams();
  const next = sp.get("next");
  const navigate = useNavigate();
  const [tab, setTab] = useState("register");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", company_group: "", company_group_other: "" });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/user/forgot-password", { email: forgotEmail, origin_url: window.location.origin });
      setForgotSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo procesar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  // Procesa la vuelta de Google Auth (session_id en el fragmento de la URL)
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || !hash.includes("session_id=")) return;
    const sid = new URLSearchParams(hash.slice(1)).get("session_id");
    if (!sid) return;
    setGoogleLoading(true);
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sid });
        loginUser(data.token, data.user);
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        toast.success("Sesión iniciada con Google");
        if (next) navigate(next);
      } catch (err) {
        toast.error("No se pudo iniciar sesión con Google");
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      } finally {
        setGoogleLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/cuenta" + (next ? `?next=${encodeURIComponent(next)}` : "");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const ep = tab === "login" ? "/user/login" : "/user/register";
      const payload = tab === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(ep, payload);
      loginUser(data.token, data.user);
      toast.success(tab === "login" ? "Sesión iniciada" : "¡Cuenta creada!");
      if (next) navigate(next);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo completar");
    } finally {
      setLoading(false);
    }
  };

  if (!userToken) {
    if (forgotMode) {
      return (
        <div className="min-h-screen bg-[#F8FAFC]"><BrandHeader />
          <div className="max-w-md mx-auto px-4 py-10">
            <div className="bg-white rounded-3xl shadow-lg p-8">
              <h1 className="text-2xl font-black brand-navy text-center">Recuperar contraseña</h1>
              {forgotSent ? (
                <div className="mt-6 text-center space-y-4" data-testid="forgot-sent">
                  <p className="text-slate-600">Si existe una cuenta con ese email, te hemos enviado un enlace para restablecer tu contraseña. Revisa tu bandeja de entrada (y la carpeta de spam).</p>
                  <button data-testid="forgot-back-login" onClick={() => { setForgotMode(false); setForgotSent(false); setTab("login"); }} className="w-full bg-brand-navy text-white font-bold rounded-full py-3.5 hover:bg-[#FF7F27] transition-colors">Volver a iniciar sesión</button>
                </div>
              ) : (
                <>
                  <p className="text-center text-slate-500 mt-2 mb-6">Introduce tu correo y te enviaremos un enlace para crear una nueva contraseña.</p>
                  <form onSubmit={submitForgot} className="space-y-3">
                    <input data-testid="forgot-email" type="email" className={inputCls} placeholder="Correo electrónico" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required />
                    <button data-testid="forgot-submit" disabled={loading} className="w-full bg-brand-orange text-white font-bold rounded-full py-3.5 hover:bg-[#DE7A1C] disabled:opacity-50 transition-colors flex items-center justify-center gap-2">{loading ? <Loader2 className="animate-spin" size={18} /> : "Enviar enlace"}</button>
                  </form>
                  <button data-testid="forgot-cancel" onClick={() => setForgotMode(false)} className="mt-4 w-full text-sm font-bold text-slate-500 hover:brand-navy">Volver</button>
                </>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-[#F8FAFC]"><BrandHeader />
        <div className="max-w-md mx-auto px-4 py-10">
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <h1 className="text-2xl font-black brand-navy text-center">Mi cuenta</h1>
            <p className="text-center text-slate-500 mt-2 mb-6">Crea tu cuenta o inicia sesión para apuntarte y recibir avisos de las actividades que te interesan.</p>
            <div className="flex bg-slate-100 rounded-full p-1 mb-6">
              <button data-testid="tab-register" onClick={() => setTab("register")} className={`flex-1 py-2 rounded-full text-sm font-bold ${tab === "register" ? "bg-white brand-navy shadow" : "text-slate-500"}`}>Crear cuenta</button>
              <button data-testid="tab-login" onClick={() => setTab("login")} className={`flex-1 py-2 rounded-full text-sm font-bold ${tab === "login" ? "bg-white brand-navy shadow" : "text-slate-500"}`}>Iniciar sesión</button>
            </div>
            <form onSubmit={submitAuth} className="space-y-3">
              {tab === "register" && <input data-testid="user-name" className={inputCls} placeholder="Nombre y apellidos" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />}
              <input data-testid="user-email" type="email" className={inputCls} placeholder="Correo electrónico" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              {tab === "register" && <input data-testid="user-phone" className={inputCls} placeholder="Móvil con WhatsApp (+34 600 000 000)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />}
              {tab === "register" && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Grupo de empresa</p>
                  <div className="grid grid-cols-2 gap-2">
                    {COMPANY_GROUPS.map((g) => (
                      <button type="button" key={g} data-testid={`group-${g}`} onClick={() => setForm({ ...form, company_group: g })} className={`rounded-xl py-2.5 text-sm font-bold border-2 transition-colors ${form.company_group === g ? "border-[#003284] bg-[#003284] text-white" : "border-slate-200 text-slate-600 hover:border-[#003284]"}`}>{g}</button>
                    ))}
                  </div>
                  {form.company_group === "OTRO" && (
                    <input data-testid="group-other-input" className={inputCls + " mt-2"} placeholder="¿Cuál es tu grupo de empresa?" value={form.company_group_other} onChange={(e) => setForm({ ...form, company_group_other: e.target.value })} required />
                  )}
                </div>
              )}
              <input data-testid="user-password" type="password" className={inputCls} placeholder="Contraseña" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
              <button data-testid="user-auth-submit" disabled={loading} className="w-full bg-brand-orange text-white font-bold rounded-full py-3.5 hover:bg-[#DE7A1C] disabled:opacity-50 transition-colors flex items-center justify-center gap-2">{loading ? <Loader2 className="animate-spin" size={18} /> : (tab === "login" ? <><LogIn size={18} /> Entrar</> : <><UserPlus size={18} /> Crear cuenta</>)}</button>
            </form>
            {tab === "login" && <button data-testid="forgot-link" onClick={() => { setForgotMode(true); setForgotEmail(form.email); }} className="mt-3 w-full text-center text-sm font-bold text-[#003284] hover:text-[#FF7F27] transition-colors">¿Olvidaste tu contraseña?</button>}
            <div className="flex items-center gap-3 my-5"><div className="h-px flex-1 bg-slate-200" /><span className="text-xs font-bold text-slate-400">O</span><div className="h-px flex-1 bg-slate-200" /></div>
            <button data-testid="google-auth-btn" onClick={loginGoogle} disabled={googleLoading} className="w-full border-2 border-slate-200 hover:border-[#003284] rounded-full py-3.5 font-bold text-slate-700 flex items-center justify-center gap-3 transition-colors disabled:opacity-50">
              {googleLoading ? <Loader2 className="animate-spin" size={18} /> : <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />}
              Continuar con Google
            </button>
          </div>
        </div>
      </div>
    );
  }
  return <Profile user={user} setUser={setUser} logoutUser={logoutUser} navigate={navigate} />;
}

function MyBookings() {
  const [regs, setRegs] = useState([]);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/registrations/mine").then((r) => setRegs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const confirmCancel = async () => {
    setBusy(true);
    try {
      await api.post(`/registrations/${target.id}/request-cancellation`);
      toast.success("Solicitud de cancelación enviada. Te confirmaremos el reembolso.");
      setTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo solicitar"); }
    finally { setBusy(false); }
  };
  if (!regs.length) return null;
  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-100 space-y-3" data-testid="my-bookings">
      <h3 className="font-black brand-navy">Mis inscripciones</h3>
      {regs.map((r) => (
        <div key={r.id} data-testid={`my-booking-${r.id}`} className="border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <p className="font-bold brand-navy">{r.activity_title}</p>
              <p className="text-xs text-slate-500">{r.reference} · {r.start_date || ""} · {formatEur(r.total)}</p>
            </div>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${r.status === "cancelada" ? "bg-red-100 text-[#E31837]" : r.status === "confirmada" ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-[#FF7F27]"}`}>{r.status}</span>
          </div>
          {r.status === "cancelada" ? (
            <p className="text-xs text-slate-500 mt-2">Reembolso: {formatEur(r.refund_amount || 0)} {r.refund_status === "pending_manual" ? "(pendiente de gestión manual)" : r.refund_status === "refunded" ? "(reembolsado a tarjeta)" : ""}</p>
          ) : r.cancellation_requested ? (
            <p className="text-xs text-[#FF7F27] font-semibold mt-2">Cancelación solicitada · pendiente de aprobación</p>
          ) : (
            <button data-testid={`request-cancel-${r.id}`} onClick={() => setTarget(r)} className="mt-2 text-xs font-bold text-[#E31837] hover:text-red-700">Solicitar cancelación</button>
          )}
        </div>
      ))}
      <Dialog open={!!target} onOpenChange={(v) => !v && setTarget(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="brand-navy">Solicitar cancelación</DialogTitle></DialogHeader>
          {target && (
            <div className="space-y-3">
              <p className="font-bold brand-navy">{target.activity_title}</p>
              <div className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-4">
                <p className="text-sm text-slate-600">Importe pagado: <strong>{formatEur(target.total)}</strong></p>
                <p className="text-2xl font-black text-emerald-600 mt-1" data-testid="refund-amount">Se te devolverán {formatEur(target.refund_quote?.refund_amount || 0)}</p>
                <p className="text-sm text-slate-500 mt-2">{target.refund_quote?.reason}</p>
                {target.refund_quote?.is_manual && <p className="text-xs text-slate-400 mt-1">El reembolso se gestionará manualmente por el mismo método de pago.</p>}
              </div>
              <p className="text-xs text-slate-400">Tu solicitud quedará pendiente de aprobación por la organización.</p>
              <div className="flex gap-2">
                <button onClick={() => setTarget(null)} className="flex-1 border-2 border-slate-200 text-slate-600 font-bold rounded-full py-3">Volver</button>
                <button data-testid="confirm-cancel-request" onClick={confirmCancel} disabled={busy} className="flex-1 bg-[#E31837] text-white font-bold rounded-full py-3 disabled:opacity-50">{busy ? "Enviando…" : "Solicitar cancelación"}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Profile({ user, setUser, logoutUser, navigate }) {
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [kids, setKids] = useState(user?.kids || []);
  const [interestAll, setInterestAll] = useState(user?.interest_all !== false);
  const [cats, setCats] = useState(user?.interest_categories || []);
  const [group, setGroup] = useState(user?.company_group || "");
  const [groupOther, setGroupOther] = useState(user?.company_group_other || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || ""); setPhone(user.phone || ""); setKids(user.kids || []);
      setGroup(user.company_group || ""); setGroupOther(user.company_group_other || "");
      setInterestAll(user.interest_all !== false); setCats(user.interest_categories || []);
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/user/profile", { name, phone, company_group: group || undefined, company_group_other: groupOther, kids, interest_all: interestAll, interest_categories: cats });
      setUser(data); toast.success("Datos guardados");
    } catch (e) { toast.error("No se pudo guardar"); } finally { setSaving(false); }
  };
  const toggleCat = (id) => setCats((c) => c.includes(id) ? c.filter((x) => x !== id) : [...c, id]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]"><BrandHeader />
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-black brand-navy">Mi cuenta</h1>
          <button data-testid="logout-btn" onClick={() => { logoutUser(); navigate("/inicio"); }} className="flex items-center gap-1.5 text-sm font-bold text-[#E31837]"><LogOut size={16} /> Cerrar sesión</button>
        </div>
        <div className="bg-white rounded-3xl p-6 border border-slate-100 space-y-4">
          <p className="text-sm text-slate-400">{user?.email}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-sm font-bold text-slate-700 mb-1.5 block">Nombre</label><input data-testid="profile-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div><label className="text-sm font-bold text-slate-700 mb-1.5 block">Móvil con WhatsApp</label><input className={inputCls} placeholder="+34 600 000 000" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
          </div>
          <div>
            <label className="text-sm font-bold text-slate-700 mb-1.5 block">Grupo de empresa</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {COMPANY_GROUPS.map((g) => (
                <button type="button" key={g} data-testid={`profile-group-${g}`} onClick={() => setGroup(g)} className={`rounded-xl py-2.5 text-sm font-bold border-2 transition-colors ${group === g ? "border-[#003284] bg-[#003284] text-white" : "border-slate-200 text-slate-600 hover:border-[#003284]"}`}>{g}</button>
              ))}
            </div>
            {group === "OTRO" && (
              <input data-testid="profile-group-other-input" className={inputCls + " mt-2"} placeholder="¿Cuál es tu grupo de empresa?" value={groupOther} onChange={(e) => setGroupOther(e.target.value)} />
            )}
          </div>
        </div>
        <div className="bg-white rounded-3xl p-6 border border-slate-100 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-black brand-navy">Mis hijos</h3>
            <button data-testid="add-kid" onClick={() => setKids([...kids, { name: "", birthdate: "" }])} className="flex items-center gap-1 text-sm font-bold text-[#003284]"><Plus size={16} /> Añadir hijo/a</button>
          </div>
          <p className="text-sm text-slate-500">Indica la fecha de nacimiento de cada hijo/a. Usaremos su edad para enviarte las actividades que le corresponden según crece.</p>
          {kids.length === 0 && <p className="text-sm text-slate-400">Aún no has añadido hijos.</p>}
          {kids.map((k, i) => (
            <div key={i} className="grid sm:grid-cols-[1fr_170px_auto] gap-3 items-end">
              <div><label className="text-xs font-bold text-slate-500 mb-1 block">Nombre</label><input data-testid={`kid-name-${i}`} className={inputCls} value={k.name} onChange={(e) => { const c = [...kids]; c[i] = { ...c[i], name: e.target.value }; setKids(c); }} /></div>
              <div><label className="text-xs font-bold text-slate-500 mb-1 block">Fecha nacimiento</label><input data-testid={`kid-birth-${i}`} type="date" className={inputCls} value={k.birthdate} onChange={(e) => { const c = [...kids]; c[i] = { ...c[i], birthdate: e.target.value }; setKids(c); }} /></div>
              <button onClick={() => setKids(kids.filter((_, j) => j !== i))} className="mb-1 w-10 h-10 rounded-xl bg-red-50 text-[#E31837] flex items-center justify-center"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <MyBookings />
        <div className="bg-white rounded-3xl p-6 border border-slate-100 space-y-4">
          <h3 className="font-black brand-navy">Actividades que me interesan</h3>
          <label className="flex items-center gap-3 cursor-pointer"><input data-testid="interest-all" type="checkbox" className="w-5 h-5 accent-[#003284]" checked={interestAll} onChange={(e) => setInterestAll(e.target.checked)} /><span className="text-slate-700 font-semibold">Quiero recibir todas las actividades propuestas</span></label>
          {!interestAll && (
            <div>
              <p className="text-sm text-slate-500 mb-2">O elige solo las categorías que te interesan:</p>
              <div className="flex flex-wrap gap-2 items-center">
                {ADULT_CATEGORIES.map((c) => { const active = cats.includes(c.id); return <button key={c.id} data-testid={`interest-${c.id}`} onClick={() => toggleCat(c.id)} className={`px-3 py-1.5 rounded-full text-sm font-bold border-2 transition-colors ${active ? "border-[#003284] bg-[#003284] text-white" : "border-slate-200 text-slate-500"}`}>{c.label} <span className="opacity-70">{c.range}</span></button>; })}
                <span className="w-full text-[11px] font-bold uppercase tracking-wide text-[#FF7F27] mt-1">Familiares</span>
                {FAMILY_CATEGORIES.map((c) => { const active = cats.includes(c.id); return <button key={c.id} data-testid={`interest-${c.id}`} onClick={() => toggleCat(c.id)} className={`px-3 py-1.5 rounded-full text-sm font-bold border-2 transition-colors ${active ? "border-[#003284] bg-[#003284] text-white" : "border-slate-200 text-slate-500"}`}>{c.label} <span className="opacity-70">{c.range}</span></button>; })}
              </div>
            </div>
          )}
        </div>
        <button data-testid="save-profile" onClick={save} disabled={saving} className="w-full bg-brand-navy text-white font-black rounded-full py-4 hover:bg-[#FF7F27] disabled:opacity-50 transition-colors">{saving ? "Guardando…" : "Guardar mis datos"}</button>
      </div>
    </div>
  );
}
